/* ─────────────────────────────────────────────────────────────
   BotonImportarRutasExcel.jsx — admin only
   Importa el Excel pipas_editado.xlsx en ruta_puntos vía
   POST /api/admin/importar-rutas-maestro/desde-excel.
   Flujo: archivo → Vista previa (dry-run) → Aplicar.
   12-jun-2026 (Octavio).
   ───────────────────────────────────────────────────────────── */
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BotonImportarRutasExcel() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  if (user?.rol !== "admin") return null;

  return (
    <>
      <div className="mt-6 p-4 bg-white border border-slate-200 rounded-lg" data-testid="seccion-importar-rutas">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-bold text-slate-800">
              <i className="fa-solid fa-file-excel mr-2 text-emerald-600"/>
              Importar / Exportar rutas
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Maneja el archivo <code className="bg-slate-100 px-1 rounded">pipas_editado.xlsx</code> en ambos sentidos (descargar → editar → reimportar).
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <BotonDescargarRutas/>
            <button
              onClick={() => setOpen(true)}
              data-testid="btn-abrir-importar-rutas"
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded px-4 py-2 text-sm"
            >
              <i className="fa-solid fa-upload mr-2"/> Importar Excel
            </button>
          </div>
        </div>
      </div>

      {open && <ModalImportar onClose={() => setOpen(false)}/>}
    </>
  );
}


function BotonDescargarRutas() {
  const [bajando, setBajando] = useState(false);
  const descargar = async () => {
    setBajando(true);
    try {
      const r = await axios.get(`${API}/admin/exportar-rutas-maestro/excel`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, "");
      a.download = `ruta_puntos_export_${ts}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Archivo descargado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al descargar");
    } finally {
      setBajando(false);
    }
  };
  return (
    <button
      onClick={descargar}
      disabled={bajando}
      data-testid="btn-descargar-rutas"
      className="bg-slate-700 hover:bg-slate-800 text-white font-bold rounded px-4 py-2 text-sm disabled:opacity-60"
    >
      <i className="fa-solid fa-download mr-2"/>
      {bajando ? "Generando…" : "Descargar ruta actual"}
    </button>
  );
}


export function ModalImportar({ onClose }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);   // resultado dry-run
  const [aplicado, setAplicado] = useState(null); // resultado final
  const [busy, setBusy] = useState(false);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.xlsx?$/i.test(f.name)) {
      toast.error("Solo archivos .xlsx");
      return;
    }
    if (f.size > 20 * 1024 * 1024) {
      toast.error("El archivo supera 20 MB");
      return;
    }
    setFile(f);
    setPreview(null);
    setAplicado(null);
  };

  const _enviar = async (confirmar) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (confirmar) fd.append("confirmar", "true");
      const r = await axios.post(`${API}/admin/importar-rutas-maestro/desde-excel`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (confirmar) {
        setAplicado(r.data);
        toast.success(`Importación aplicada: ${r.data.creados} creados, ${r.data.actualizados} actualizados, ${r.data.sin_cambio} sin cambio`);
      } else {
        setPreview(r.data);
        if (r.data.ok && (r.data.items_parseados || 0) > 0) {
          toast.success(`Vista previa lista: ${r.data.items_parseados} items detectados`);
        } else {
          toast.error("El parser no encontró filas válidas. Revisa la estructura del Excel.");
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al procesar el Excel");
    } finally {
      setBusy(false);
    }
  };

  const vistaPrevia = () => _enviar(false);
  const aplicar = () => _enviar(true);

  return (
    <div
      onClick={onClose}
      data-testid="modal-importar-rutas-bg"
      style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,0.7)",
        zIndex: 9000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="modal-importar-rutas"
        style={{
          background: "white", borderRadius: 14, width: "100%", maxWidth: 760,
          maxHeight: "92vh", overflow: "auto",
          boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
        }}
      >
        {/* Header */}
        <header style={{ padding: "16px 20px", borderBottom: "1px solid #E2E8F0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#0F172A" }}>
              <i className="fa-solid fa-file-excel mr-2 text-emerald-600"/>
              Importar rutas desde Excel
            </div>
            <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>
              Sube <code>pipas_editado.xlsx</code> · hojas ZONA 1..14, CAMELLONES, TINACOS
            </div>
          </div>
          <button onClick={onClose} aria-label="Cerrar" data-testid="modal-importar-cerrar"
                  style={{ background: "transparent", border: 0, fontSize: 22, cursor: "pointer", color: "#64748B" }}>
            <i className="fa-solid fa-xmark"/>
          </button>
        </header>

        {/* Body */}
        <section style={{ padding: 20 }}>
          {/* Paso 1 — file picker */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 13, fontWeight: 700, color: "#334155", marginBottom: 6 }}>
              1) Selecciona el archivo
            </label>
            <input
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={onPick}
              data-testid="input-file-rutas"
              style={{ width: "100%", padding: "10px 12px", border: "1px dashed #94A3B8", borderRadius: 8, fontSize: 14, background: "#F8FAFC" }}
            />
            {file && (
              <div style={{ fontSize: 11, color: "#475569", marginTop: 6 }} data-testid="file-info">
                <i className="fa-solid fa-paperclip mr-1"/> {file.name} · {(file.size / 1024).toFixed(1)} KB
              </div>
            )}
          </div>

          {/* Paso 2 — vista previa */}
          {file && !aplicado && (
            <div style={{ marginBottom: 16 }}>
              <button
                onClick={vistaPrevia}
                disabled={busy}
                data-testid="btn-vista-previa"
                style={{
                  background: "#1D4ED8", color: "white", border: 0, padding: "10px 16px",
                  borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: busy ? "wait" : "pointer",
                }}
              >
                <i className="fa-solid fa-magnifying-glass mr-2"/>
                {busy && !preview ? "Procesando…" : "Vista previa (dry-run)"}
              </button>
            </div>
          )}

          {/* Resumen vista previa */}
          {preview && !aplicado && (
            <ResumenPreview preview={preview}/>
          )}

          {/* Paso 3 — aplicar */}
          {preview && preview.ok && (preview.items_parseados || 0) > 0 && !aplicado && (
            <div style={{ marginTop: 16, padding: 12, background: "#FEF3C7", border: "1px solid #FCD34D", borderRadius: 8 }}>
              <div style={{ fontSize: 13, color: "#92400E", marginBottom: 10 }}>
                <i className="fa-solid fa-triangle-exclamation mr-2"/>
                <strong>¿Estás seguro?</strong> Esto modificará la colección <code>ruta_puntos</code> en producción.
              </div>
              <button
                onClick={aplicar}
                disabled={busy}
                data-testid="btn-aplicar-importacion"
                style={{
                  background: "#15803D", color: "white", border: 0, padding: "12px 18px",
                  borderRadius: 8, fontWeight: 800, fontSize: 14, cursor: busy ? "wait" : "pointer",
                }}
              >
                <i className="fa-solid fa-check mr-2"/>
                {busy ? "Aplicando…" : `Aplicar importación (${preview.creados} crear + ${preview.actualizados} actualizar)`}
              </button>
            </div>
          )}

          {/* Resultado final */}
          {aplicado && <ResumenAplicado aplicado={aplicado} onClose={onClose}/>}
        </section>
      </div>
    </div>
  );
}


function ResumenPreview({ preview }) {
  const tieneErrores = (preview.errores || []).length > 0;
  const tieneAdvertencias = (preview.parser_advertencias || []).length > 0;
  return (
    <div data-testid="preview-resumen" style={{ background: "#F8FAFC", padding: 14, borderRadius: 8, border: "1px solid #E2E8F0" }}>
      <div style={{ fontWeight: 800, color: "#0F172A", marginBottom: 8, fontSize: 14 }}>
        <i className="fa-solid fa-eye mr-2 text-blue-600"/> Vista previa (no aplica cambios)
      </div>

      {/* Conteos */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 12 }} data-testid="preview-conteos">
        <Stat label="Items detectados" value={preview.items_parseados || 0} color="#0F172A"/>
        <Stat label="A crear" value={preview.creados || 0} color="#15803D"/>
        <Stat label="A actualizar" value={preview.actualizados || 0} color="#9A3412"/>
        <Stat label="Sin cambio" value={preview.sin_cambio || 0} color="#475569"/>
      </div>

      {/* Resumen por hoja */}
      {preview.resumen_por_hoja && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#475569", marginBottom: 6 }}>Hojas detectadas:</div>
          <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }} data-testid="preview-tabla-hojas">
            <thead>
              <tr style={{ background: "#E2E8F0" }}>
                <th style={{ padding: "6px 8px", textAlign: "left" }}>Hoja</th>
                <th style={{ padding: "6px 8px", textAlign: "left" }}>Zona</th>
                <th style={{ padding: "6px 8px", textAlign: "left" }}>Chofer</th>
                <th style={{ padding: "6px 8px", textAlign: "left" }}>Pipa</th>
                <th style={{ padding: "6px 8px", textAlign: "center" }}>Items</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(preview.resumen_por_hoja).map(([hoja, info]) => (
                <tr key={hoja} style={{ borderTop: "1px solid #E2E8F0" }}>
                  <td style={{ padding: "6px 8px", fontWeight: 700 }}>{hoja}</td>
                  <td style={{ padding: "6px 8px", color: "#1E40AF", fontWeight: 700 }}>{info.zona_id || "—"}</td>
                  <td style={{ padding: "6px 8px" }}>{info.chofer || "—"}</td>
                  <td style={{ padding: "6px 8px" }}>{info.pipa ? `#${info.pipa}` : "—"}</td>
                  <td style={{ padding: "6px 8px", textAlign: "center", fontWeight: 700 }}>{info.items}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Advertencias del parser */}
      {tieneAdvertencias && (
        <div style={{ marginBottom: 8, padding: 8, background: "#FEF3C7", borderRadius: 6, fontSize: 12, color: "#92400E" }} data-testid="preview-advertencias">
          <strong>Advertencias del parser ({preview.parser_advertencias.length}):</strong>
          <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
            {preview.parser_advertencias.slice(0, 8).map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}

      {/* Errores */}
      {tieneErrores && (
        <div style={{ padding: 8, background: "#FEE2E2", borderRadius: 6, fontSize: 12, color: "#B91C1C" }} data-testid="preview-errores">
          <strong>Errores ({preview.errores.length}):</strong>
          <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
            {preview.errores.slice(0, 5).map((e, i) => <li key={i}>idx={e.idx}: {e.error}</li>)}
            {preview.errores.length > 5 && <li>...y {preview.errores.length - 5} más</li>}
          </ul>
        </div>
      )}

      {(preview.items_parseados || 0) === 0 && (
        <div style={{ padding: 12, background: "#FEE2E2", color: "#B91C1C", borderRadius: 6, fontSize: 13 }}>
          ⚠️ No se detectaron items válidos. Revisa que las hojas tengan el formato esperado.
        </div>
      )}
    </div>
  );
}


function ResumenAplicado({ aplicado, onClose }) {
  // Log de errores fuera del render para no spamear en re-renders.
  useEffect(() => {
    if ((aplicado.errores || []).length > 0) {
      console.warn("Errores importación:", aplicado.errores);
    }
  }, [aplicado]);
  return (
    <div data-testid="aplicado-resumen" style={{ background: "#DCFCE7", border: "1px solid #15803D", borderRadius: 8, padding: 16 }}>
      <div style={{ fontWeight: 800, color: "#14532D", fontSize: 16, marginBottom: 10 }}>
        <i className="fa-solid fa-circle-check mr-2"/>
        Importación aplicada con éxito
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 12 }}>
        <Stat label="Items procesados" value={aplicado.items_parseados || 0} color="#0F172A"/>
        <Stat label="Creados" value={aplicado.creados || 0} color="#15803D"/>
        <Stat label="Actualizados" value={aplicado.actualizados || 0} color="#9A3412"/>
        <Stat label="Sin cambio" value={aplicado.sin_cambio || 0} color="#475569"/>
      </div>
      {(aplicado.errores || []).length > 0 && (
        <div style={{ padding: 8, background: "#FEE2E2", borderRadius: 6, fontSize: 12, color: "#B91C1C", marginBottom: 10 }}>
          {aplicado.errores.length} errores ocurrieron (ver consola).
        </div>
      )}
      <button
        onClick={onClose}
        data-testid="btn-cerrar-aplicado"
        style={{ background: "#1D4ED8", color: "white", border: 0, padding: "10px 16px", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer" }}
      >
        Cerrar
      </button>
    </div>
  );
}


function Stat({ label, value, color }) {
  return (
    <div style={{ background: "white", borderRadius: 6, padding: 10, textAlign: "center", border: "1px solid #E2E8F0" }}>
      <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
      <div style={{ fontSize: 10, color: "#64748B", textTransform: "uppercase", letterSpacing: 0.5, marginTop: 2 }}>{label}</div>
    </div>
  );
}
